import axiosClient from '../../api/axiosClient';

/**
 * Sorts unanswered prompts most-recent-first. Display-layer concerns (e.g.
 * capping how many pagination dots are shown at once) belong in the
 * component, not here — this returns the full list.
 * @param {Array} prompts
 * @returns {Array}
 */
export function sortPromptsMostRecentFirst(prompts) {
  return [...prompts].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
}

/**
 *
 * Prompt model
 */
const promptModel = {
  state: {
    isLoading: false,
    isPromptResponseLoading: false,
    recentPrompt: {},
    unansweredPrompts: [],
    promptResponse: {},
    allPrompts: [],
    interactedPairConnectionResponse: null,
    interactedMultiChoiceResponse: null,
  },
  reducers: {
    setData(state, payload) {
      return { ...state, [payload.name]: payload.data };
    },
    removeUnansweredPrompt(state, promptId) {
      return { ...state, unansweredPrompts: state.unansweredPrompts.filter(prompt => prompt._id !== promptId) };
    },
  },
  effects: dispatch => ({
    /**
     * Returns most recent created prompt
     * @returns {Promise<void>}
     */
    async getMostRecentPrompt() {
      try {
        this.setData({
          name: 'isLoading',
          data: true,
        });
        const response = await axiosClient.get('/prompt?mostRecent=true&notInteractedOnly=true');
        if (response.data.code === 200) {
          this.setData({
            name: 'recentPrompt',
            data: response?.data?.data?.prompts[0],
          });
        } else {
          this.setData({
            name: 'recentPrompt',
            data: [],
          });
        }
      } catch (err) {
        dispatch.toastModel.showToast({
          value: 'Danger',
          message: err.message || 'Failed to fetch the most recent prompt',
        });
      } finally {
        this.setData({
          name: 'isLoading',
          data: false,
        });
      }
    },

    /**
     * Returns all unanswered prompts for the current user
     * @returns {Promise<void>}
     */
    async getUnansweredPrompts() {
      try {
        this.setData({
          name: 'isLoading',
          data: true,
        });
        const response = await axiosClient.get('/prompt?notInteractedOnly=true');
        if (response.data.code === 200) {
          this.setData({
            name: 'unansweredPrompts',
            data: sortPromptsMostRecentFirst(response?.data?.data?.prompts || []),
          });
        } else {
          this.setData({
            name: 'unansweredPrompts',
            data: [],
          });
        }
      } catch (err) {
        dispatch.toastModel.showToast({
          value: 'Danger',
          message: err.message || 'Failed to fetch unanswered prompts',
        });
      } finally {
        this.setData({
          name: 'isLoading',
          data: false,
        });
      }
    },

    /**
     * Returns users prompt response for promptId
     * @returns {Promise<void>}
     */
    async getPromptResponse(payload) {
      try {
        const { promptId, userId } = payload;
        this.setData({
          name: 'isPromptResponseLoading',
          data: true,
        });
        const response = await axiosClient.get(`/prompt/${userId}/${promptId}`);

        if (response.data.code === 200) {
          this.setData({
            name: 'promptResponse',
            data: response?.data?.data?.promptResponse,
          });
        } else {
          this.setData({
            name: 'promptResponse',
            data: [],
          });
        }
      } catch (err) {
        dispatch.toastModel.showToast({
          value: 'Danger',
          message: err.message || 'Failed to fetch promptResponse',
        });
      } finally {
        this.setData({
          name: 'isPromptResponseLoading',
          data: false,
        });
      }
    },

    /**
     * Returns all prompts
     * @returns {Promise<void>}
     */
    async getAllPrompts() {
      try {
        const response = await axiosClient.get('/prompt');
        if (response.data.code === 200) {
          this.setData({
            name: 'allPrompts',
            data: response.data.data.prompts,
          });
        }
      } catch (err) {
        dispatch.toastModel.showToast({
          value: 'Danger',
          message: err.message || 'Failed to fetch all prompts',
        });
      }
    },

    /**
     * Updates nudge prompt response
     * @returns {Promise<void>}
     */
    async interactWithNudgePrompt(payload) {
      try {
        const { promptId } = payload;
        const response = await axiosClient.patch(`/prompt/${promptId}/nudge`, payload);
        if (response.data.code === 200) {
          return response.data;
        } else {
          dispatch.toastModel.showToast({
            value: 'Danger',
            message: response.data.msg || 'Failed to update nudge prompt.',
          });
        }
      } catch (err) {
        dispatch.toastModel.showToast({
          value: 'Danger',
          message: err.message || 'Failed to update nudge prompt.',
        });
      }
    },

    /**
     * Updates open-response prompt response
     * @returns {Promise<void>}
     */
    async interactWithOpenResponsePrompt(payload) {
      try {
        const { promptId } = payload;
        const response = await axiosClient.patch(`/prompt/${promptId}/open-response`, payload);

        if (response && response.data && response.data?.code === 200) {
          return response.data;
        } else {
          dispatch.toastModel.showToast({
            value: 'Danger',
            message: response.data.msg || 'Failed to update open-response prompt.',
          });
        }
      } catch (err) {
        dispatch.toastModel.showToast({
          value: 'Danger',
          message: err.message || 'Failed to update open-response prompt.',
        });
      }
    },

    /**
     * Updates pair-connection prompt response
     * @param payload - {object}
     * @returns {Promise<void>}
     */
    async interactWithPairConnectionPrompt(payload) {
      try {
        const { promptId } = payload;
        const response = await axiosClient.patch(`/prompt/${promptId}/pair-connection`, payload);

        if (response && response.data && response.data?.code === 200) {
          this.setData({
            name: 'interactedPairConnectionResponse',
            data: response?.data,
          });
          return response.data;
        } else {
          dispatch.toastModel.showToast({
            value: 'Danger',
            message: response.data.msg || 'Failed to update pair-connection prompt.',
          });
        }
      } catch (err) {
        dispatch.toastModel.showToast({
          value: 'Danger',
          message: err.message || 'Failed to update pair-connection prompt.',
        });
      }
    },

    /**
     * Updates multi-choice prompt response
     * @param payload - {object}
     * @returns {Promise<void>}
     */
    async interactWithMultiChoicePrompt(payload) {
      try {
        const { promptId } = payload;
        const response = await axiosClient.patch(`/prompt/${promptId}/multi-choice`, payload);

        if (response && response.data && response.data?.code === 200) {
          this.setData({
            interactedMultiChoiceResponse: response?.data,
          });
          return response?.data;
        } else {
          dispatch.toastModel.showToast({
            value: 'Danger',
            message: response.data.msg || 'Failed to update multi-choice prompt.',
          });
        }
      } catch (err) {
        dispatch.toastModel.showToast({
          value: 'Danger',
          message: err.message || 'Failed to update multi-choice prompt.',
        });
      }
    },
  }),
};

export default promptModel;
